/**
 * Module: OrderGiftCard
 * 
 * Purpose:
 * This module provides a modular and maintainable approach to handling gift card orders,
 * including form validation, payment processing via Stripe, and analytics events via PostHog.
 * 
 * Features:
 *  - Form validation using JustValidate
 *  - Payment intent management with Stripe
 *  - Storage and retrieval of gift card data in localStorage
 *  - Clean error handling with async/await
 *  - Spinner/loader management with a finally block
 *  - Analytics event capture for gift card purchases
 */

import JustValidate from 'just-validate';
import { Stripe, loadStripe } from '@stripe/stripe-js';

// Posthog
import ph from '../../analytics/posthog/posthog-client';

declare let core_object: any;
declare let gift_card_object: any;

/**
 * Determine whether to load the test or live Stripe key.
 */
const type_stripe: string = core_object?.ph_key?.type;

const ph_key: string = type_stripe === 'test' ? core_object.ph_key.test : core_object.ph_key.live;

// Load Stripe instance once
const stripe: Stripe | null = await loadStripe(ph_key);

// Stripe Elements reference
let elements;

// Payment Intent ID reference
let paymentIntentId: string | undefined;

/**
 * Utility function: Safely get an HTML element by selector.
 * @param selector - The CSS selector.
 * @returns The found element or null.
 */
function getElement<T extends HTMLElement>(selector: string): T | null {
  
    return document.querySelector(selector) as T | null;

}

/**
 * Utility function: Retrieve a string value from local storage.
 * @param key The local storage key.
 * @returns The stored string or null.
 */
function getFromLocalStorage(key: string): string | null{

    return localStorage.getItem(key);
    
}

/**
 * Utility function: Save a string value to local storage.
 * @param key The local storage key.
 * @param value The value to store.
 */
function setToLocalStorage(key: string, value: string): void {

    localStorage.setItem(key, value);
    
}

/**
 * Utility function: Remove items from local storage.
 * @param keys An array of keys to remove.
 */
function removeFromLocalStorage(keys: string[]): void {

    keys.forEach((key) => localStorage.removeItem(key));
    
}

/**
 * Extract gift card name and price based on the card type.
 * @param cardValue - The string value of the selected card.
 * @returns An object containing the name and price for the gift card.
 */
function getGiftCardDetails(cardValue: string): { name: string; price: string }{
  
    switch(cardValue){
        
        case '1':
            
            return { name: 'Gift Card SILVER', price: '15€' };
        case '2':

            return { name: 'Gift Card GOLD', price: '50€' };
        
        case '3':

            return { name: 'Gift Card PLATINUM', price: '150€' };
        
        default:

            return { name: '', price: '' };
        
    }
    
}

/**
 * Class: OrderGiftCard
 * 
 * Purpose:
 * Encapsulates all logic for handling gift card orders, including form interactions,
 * payment initialization, and finalization.
 */
export default class OrderGiftCard {

    /**
     * CSS selector for the form element.
     */
    protected elForm: string;

    /**
     * CSS selector for the primary button within the form.
     */
    protected elFormBtn: string;

    /**
     * The actual HTML form element reference.
     */
    protected paymentForm: HTMLFormElement | null;

    // API endpoints
    protected urlApiInit: string;
    protected urlApiOrder: string;
    protected urlApiPaymentInit: string;

    /**
     * Constructor
     * @param elForm - Selector for the main payment form.
     * @param elFormBtn - Selector for the primary payment button.
     */
    constructor(elForm: string, elFormBtn: string) {
        
        this.elForm = elForm;
        this.elFormBtn = elFormBtn;
        this.paymentForm = getElement<HTMLFormElement>(this.elForm);

        this.urlApiInit = gift_card_object.rest_urls.gift_card_init;
        this.urlApiOrder = gift_card_object.rest_urls.gift_card_order;
        this.urlApiPaymentInit = gift_card_object.rest_urls.gift_card_payment_init;

    }

    /**
     * Initialize form validation with JustValidate.
     */
    public validateForm(): void {
    
        const form = getElement<HTMLFormElement>(this.elForm);
        if (!form) return;

        const wrapPayment = getElement<HTMLElement>('#wrap-payment-info');
        const buttonStep = getElement<HTMLButtonElement>('#gift-card-payment-button-step-1');
        const buttonText = getElement<HTMLButtonElement>('#gift-card-payment-button-text');

        // Show the form (e.g., removing spinners if any)
        this.showForm(form);

        // Configure JustValidate
        const validation = new JustValidate(this.elForm, {
            errorFieldCssClass: 'is-invalid border border-red-300',
            errorLabelCssClass: 'just-validate-error-label flex flex-row flex-wrap basis-full w-full mt-1 !text-red-300',
            focusInvalidField: true,
            lockForm: true,
        });

        // Add all required fields
        validation
        .addField('#field_name_recipient', [
            {
            rule: 'required',
            errorMessage: gift_card_object.error_messages.field_required,
            },
        ])
        .addField('#field_mail_recipient', [
            {
            rule: 'required',
            errorMessage: gift_card_object.error_messages.field_required,
            },
            {
            rule: 'email',
            errorMessage: gift_card_object.error_messages.email_invalid,
            },
        ])
        .addField('#field_message_recipient', [
            {
            rule: 'required',
            errorMessage: gift_card_object.error_messages.field_required,
            },
        ])
        .addField('#field_name_sender', [
            {
            rule: 'required',
            errorMessage: gift_card_object.error_messages.field_required,
            },
        ])
        .addField('#field_mail_sender', [
            {
            rule: 'required',
            errorMessage: gift_card_object.error_messages.field_required,
            },
            {
            rule: 'email',
            errorMessage: gift_card_object.error_messages.email_invalid,
            },
        ])
        .addField('#field_privacy_policy', [
            {
            rule: 'required',
            errorMessage: gift_card_object.error_messages.check_privacy_policy,
            },
        ])
        .addField('#field_terms_conditions', [
            {
            rule: 'required',
            errorMessage: gift_card_object.error_messages.check_term_condition,
            },
        ])
        .onSuccess((event: Event) => {
            event.preventDefault();

            // Reveal payment UI elements
            wrapPayment?.classList.remove('hidden');
            buttonStep?.classList.add('hidden');
            buttonText?.classList.remove('hidden');

            // Store the selected card details
            this.getCard();

            // Attach the submit handler
            this.handleSubmit(form);
        });
      
    }

    /**
     * Mount the payment form when needed.
     */
    public mountPaymentForm(): void {

        const btnMount = getElement<HTMLButtonElement>('#py_mount');

        btnMount?.addEventListener('click', (e) => {
            e.preventDefault();
            
            if(this.paymentForm){
                
                this.showForm(this.paymentForm);

            }

        });

    }

    /**
     * Unmount the payment form.
     * @param paymentElement - The PaymentElement instance.
     */
    public unmountPaymentForm(paymentElement): void {
        
        const btnUnmount = getElement<HTMLButtonElement>('#py_umount');

        btnUnmount?.addEventListener('click', (e) => {

            e.preventDefault();
            
            paymentElement.unmount();
        });

    }

    /**
     * Retrieve and store the initially checked gift card from the DOM.
     */
    public getCard(): void {
        
        const cardInputs = document.querySelectorAll<HTMLInputElement>('.field_gift_card');

        cardInputs.forEach((checkbox) => {

            if(checkbox.checked){

                const { name, price } = getGiftCardDetails(checkbox.value);
                setToLocalStorage('gift-card-name', name);
                setToLocalStorage('gift-card-price', price);

            }
            
        });
    }

    /**
     * Handle changes on the gift card checkboxes.
     * @param paymentElement - The PaymentElement instance to be re-mounted on change.
     */
    public changeCard(paymentElement): void {
        
        const cardInputs = document.querySelectorAll<HTMLInputElement>('.field_gift_card');
        const cardLabel = getElement<HTMLElement>('#gift-card-select-text');

        cardInputs.forEach((checkbox) => {

            checkbox.addEventListener('change', (event) => {
                
                const input = event.target as HTMLInputElement;
                setToLocalStorage('gift-card', input.value);

                // Update displayed card label
                if(cardLabel && typeof input.dataset.text !== 'undefined'){

                    cardLabel.innerHTML = input.dataset.text;

                }

                // Update local storage with new card details
                const { name, price } = getGiftCardDetails(input.value);
                setToLocalStorage('gift-card-name', name);
                setToLocalStorage('gift-card-price', price);

                // Unmount and remount payment element
                paymentElement.unmount();

                setTimeout(() => {

                    if(this.paymentForm){
                        
                        this.showForm(this.paymentForm);

                    }

                }, 500);

            });
            
        });

    }

    /**
     * Show the payment form (hide spinner) and initialize or check the payment status.
     * @param form - The payment form element.
     */
    protected showForm(form: HTMLFormElement): void {
        
        const clientSecretParam = new URLSearchParams(window.location.search).get('payment_intent_client_secret');

        // Show/hide spinner
        this.setProcessing(true, form);

        if(!clientSecretParam){
            
            this.setProcessing(false, form);
            this.initialize(); // Create PaymentIntent & mount elements

        }

        // Check the PaymentIntent status if the client secret is present in the URL
        this.checkStatus();

    }

    /**
     * Show or hide the loader/spinner for the payment form.
     * @param isProcessing - Whether the form is currently processing.
     * @param paymentForm - The form element.
     */
    protected setProcessing(isProcessing: boolean, paymentForm: HTMLFormElement): void {

        const processEl = getElement<HTMLElement>('#gift-card-form-process');

        if(isProcessing){

            paymentForm.classList.add('hidden');
            processEl?.classList.remove('hidden');
            
        }else{

            paymentForm.classList.remove('hidden');
            processEl?.classList.add('hidden');

        }

    }

    /**
     * Fetch a new payment intent from the server and mount the Stripe PaymentElement.
     */
    protected async initialize(): Promise<void> {
        
        try{

            const cardValue = this.resolveGiftCardValue();

            const response = await fetch(this.urlApiPaymentInit, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                request_type: 'create_payment_intent',
                gift_card: cardValue,
                }),
            });

            const { id, clientSecret } = await response.json();

            if(stripe){

                elements = stripe.elements({ clientSecret });
                elements.update({
                    appearance: {
                        theme: 'night',
                        labels: 'floating',
                    },
                });

                const paymentOptions = { layout: 'accordion' as const };
                const paymentElement = elements.create('payment', paymentOptions);
                paymentElement.mount('#gift-card-payment-element');

                // Hide loading once the payment element is ready
                paymentElement.on('ready', () => {
                const loadingEl = getElement<HTMLElement>('#gift-card-loading-payment');
                loadingEl?.classList.add('hidden');
                });

                paymentIntentId = id;

                // Enable dynamic card switching
                this.changeCard(paymentElement);

            }

        }catch(error){

            console.error('Error initializing payment:', error);
            
            this.showMessage(gift_card_object?.payment_error_messages?.general ?? 'An error occurred while initializing the payment.');
            
        }
        
    }

    /**
     * Check the PaymentIntent status from Stripe (via the client secret in the URL).
     */
    protected async checkStatus(): Promise<void> {
      
        const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');
        const customerId = new URLSearchParams(window.location.search).get('customer_id');

        if(!clientSecret || !stripe) return;

        try{
            
            const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

            if(paymentIntent){
                
                switch (paymentIntent.status) {

                    case 'succeeded':
                        
                        await this.handlePaymentSucceeded(paymentIntent, customerId);

                    break;
                    
                    case 'processing':
                        
                        this.showMessage(gift_card_object.payment_error_messages.processing);
                        this.setReinit();

                    break;
                    
                    case 'requires_payment_method':
                        
                        this.showMessage(gift_card_object.payment_error_messages.payment_failed);
                        this.setReinit();

                    break;
                    
                    default:
                        
                        this.showMessage(gift_card_object.payment_error_messages.general);
                        this.setReinit();

                    break;
                    
                }

            }else{
                
                this.showMessage(gift_card_object.payment_error_messages.general);
                this.setReinit();

            }
                
        }catch (error){

            console.error('Error checking payment status:', error);
            this.showMessage(gift_card_object.payment_error_messages.general);
            this.setReinit();

        }

    }

    /**
     * Handle logic when paymentIntent status is 'succeeded'.
     * @param paymentIntent - Stripe PaymentIntent object.
     * @param customerId - The ID of the customer from the query params.
     */
    protected async handlePaymentSucceeded(paymentIntent, customerId: string | null): Promise<void> {
        
        const storedOrder = getFromLocalStorage('gift-card-order');
        const giftCardName = getFromLocalStorage('gift-card-name') ?? '';
        const giftCardPrice = getFromLocalStorage('gift-card-price') ?? '';

        let giftCardId = '';
        let recipientName = '';
        let recipientMail = '';
        let recipientMessage = '';
        let senderName = '';
        let senderMail = '';

        if(storedOrder){

            try{
                
                const userData = JSON.parse(storedOrder);
                giftCardId = userData.gift_card;
                recipientName = userData.recipient_name;
                recipientMail = userData.recipient_mail;
                recipientMessage = userData.recipient_message;
                senderName = userData.sender_name;
                senderMail = userData.sender_email;

            }catch (parseError){
                
                console.error('Error parsing stored order data:', parseError);
                
            }

        }

        // Post the transaction info
        try {

            const response = await fetch(this.urlApiPaymentInit, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                request_type: 'payment_insert',
                payment_intent: paymentIntent,
                customer_id: customerId,
                gift_card: giftCardId,
                recipient_name: recipientName,
                recipient_mail: recipientMail,
                recipient_message: recipientMessage,
                sender_name: senderName,
                sender_email: senderMail,
                }),
            });

            const data = await response.json();

            if(data.payment_txn_id !== 0){
                
                // Track event via PostHog
                ph.captureGiftCardPurchase(
                    giftCardId,
                    giftCardName,
                    giftCardPrice,
                    recipientName,
                    recipientMail,
                    recipientMessage,
                    senderName,
                    senderMail
                );

                // Clear local storage
                removeFromLocalStorage([
                    'gift-card',
                    'gift-card-order',
                    'gift-card-name',
                    'gift-card-price',
                ]);

                // Redirect to completion page
                const urlRedirect = `${gift_card_object.page_complete}?pid=${data.payment_txn_id}`;
                window.location.replace(urlRedirect);

                // Dopo il redirect, rimuove anche la pagina corrente dalla cronologia
                history.replaceState(null, '', urlRedirect);

            }else{
                
                this.showMessage(data.error);
                this.setReinit();

            }

        }catch(error){

            console.error('Error inserting payment record:', error);
            this.showMessage(gift_card_object.payment_error_messages.general);
            this.setReinit();

        }

    }

    /**
     * Attach a submit handler to the payment form. Confirms payment with Stripe.
     * @param form - The payment form element.
     */
    public handleSubmit(form: HTMLFormElement): void {
      
        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            // Always manage spinner state with a try/finally to ensure it's removed.
            try{
            
                this.setLoading(true);

                const cardValue = this.resolveGiftCardValue();
                const recipientName = getElement<HTMLInputElement>('#field_name_recipient')?.value ?? '';
                const recipientMail = getElement<HTMLInputElement>('#field_mail_recipient')?.value ?? '';
                const recipientMessage = getElement<HTMLTextAreaElement>('#field_message_recipient')?.value ?? '';
                const senderName = getElement<HTMLInputElement>('#field_name_sender')?.value ?? '';
                const senderEmail = getElement<HTMLInputElement>('#field_mail_sender')?.value ?? '';

                // Store all form data in local storage
                const userObj = {
                    gift_card: cardValue,
                    recipient_name: recipientName,
                    recipient_mail: recipientMail,
                    recipient_message: recipientMessage,
                    sender_name: senderName,
                    sender_email: senderEmail,
                };

                setToLocalStorage('gift-card-order', JSON.stringify(userObj));

                // Create customer
                const createCustomerResponse = await fetch(this.urlApiPaymentInit, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        request_type: 'create_customer',
                        payment_intent_id: paymentIntentId,
                        gift_card: cardValue,
                        sender_name: userObj?.sender_name,
                        sender_email: userObj?.sender_email,
                    }),
                });

                const { customer_id } = await createCustomerResponse.json();

                // Confirm payment with Stripe
                if(stripe && elements){
                    
                    const { error } = await stripe.confirmPayment({

                        elements,
                        confirmParams: {

                            return_url: window.location.href + '?customer_id=' + customer_id,

                        },

                    });

                    // Immediate error handling
                    if(error){
                        
                        if(error.type === 'card_error' || error.type === 'validation_error'){
                            
                            if(typeof error.message !== 'undefined'){
                        
                                this.showMessage(error.message);
                                
                            }

                        }else{

                            this.showMessage('An unexpected error occurred.');
                            
                        }

                    }
                
                }
            
            }catch (error){
                
                console.error('Error submitting payment:', error);
                this.showMessage(gift_card_object.payment_error_messages.general);
                
            }finally{
                
                this.setLoading(false);
                
            }
            
        });
      
    }

    /**
     * Display a message to the user.
     * @param messageText - The message to display.
     */
    protected showMessage(messageText: string): void {

        const messageContainer = getElement<HTMLElement>('#gift-card-payment-response');

        if (!messageContainer) return;

        messageContainer.classList.remove('hidden');
        messageContainer.textContent = messageText;

        setTimeout(() => {
            messageContainer.classList.add('hidden');
            messageContainer.textContent = '';
        }, 5000);

    }

    /**
     * Show or hide the global spinner on the payment button.
     * @param isLoading - Whether to show the loading spinner.
     */
    protected setLoading(isLoading: boolean): void {
        
        const submitBtn = getElement<HTMLButtonElement>(this.elFormBtn);
        const spinner = getElement<HTMLElement>('#gift-card-payment-spinner');
        const buttonText = getElement<HTMLElement>('#gift-card-payment-button-text');

        if (!submitBtn || !spinner || !buttonText) return;

        if(isLoading){

            submitBtn.disabled = true;
            spinner.classList.remove('hidden');
            buttonText.classList.add('hidden');
            
        }else{

            submitBtn.disabled = false;
            spinner.classList.add('hidden');
            buttonText.classList.remove('hidden');

        }

    }

    /**
     * Show the "re-init payment" button, allowing the user to try again.
     */
    protected setReinit(): void {

        const formProcess = getElement<HTMLElement>('#gift-card-form-process');
        const payReinit = getElement<HTMLElement>('#gift-card-pay-reinit');

        formProcess?.classList.add('hidden');
        payReinit?.classList.remove('hidden');

    }

    /**
     * Resolve the currently selected gift card's numeric value.
     * @returns The gift card value as a number.
     */
    protected resolveGiftCardValue(): number{
      
    const storedValue = getFromLocalStorage('gift-card');
    const cardInputs = document.querySelectorAll<HTMLInputElement>('.field_gift_card');
    const cardLabel = getElement<HTMLElement>('#gift-card-select-text');

    let value = 0;

    // If something is stored, ensure we mark it checked
    if(storedValue){
      
        cardInputs.forEach((input) => {

        if(input.value === storedValue){
            
          input.checked = true;
          value = parseInt(input.value, 10);

            if(cardLabel && typeof input.dataset.text !== 'undefined'){
              
              cardLabel.innerHTML = input.dataset.text;
              
            }
            
        }
            
      });
        
    }else if (cardInputs.length > 0){
        
        // Otherwise check the first one by default
        const firstInput = cardInputs[0];
        firstInput.checked = true;
        value = parseInt(firstInput.value, 10);
        
        if(cardLabel && typeof firstInput.dataset.text !== 'undefined'){
        
            cardLabel.innerHTML = firstInput.dataset.text;
            
        }
        
    }

      return value;
      
  }
    
}
